"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import ToggleTheme from "@/components/common/Toggle/ToggleTheme";
import { MOBILE_NAV_ITEMS } from "@/contants";
import {
  hideNavItemsVariant,
  mobileMenuVariant,
  fadeInVariant,
  ulVariant,
  liVariant,
  logoVariant,
  menuIconVariant,
  bannerImageVariant,
  overlayVariant,
  textElementVariant,
  INITIAL_STATES,
  FADE_IN_TRANSITION,
  HOVER_SPRING,
  MICRO_INTERACTION,
  SMOOTH_TRANSITION,
} from "@/contants/header";

export default function Header() {
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  return (
    <div className="overflow-hidden text-black dark:text-white">
      <motion.nav
        className="flex justify-between items-center p-4 md:px-[45px] md:py-[35px] bg-transparent absolute z-50 w-full"
        initial="closed"
        animate={mobileNavOpen ? "opened" : "closed"}
        layout
      >
        <motion.div className="overflow-hidden" layout>
          <motion.div
            initial={INITIAL_STATES.fade_in_start}
            animate={INITIAL_STATES.fade_in_end}
            transition={FADE_IN_TRANSITION}
            className="w-[60px] h-[60px]"
            variants={hideNavItemsVariant}
          >
            <motion.div
              variants={logoVariant}
              whileHover={{
                scale: 1.15,
                rotate: -3,
                transition: HOVER_SPRING,
              }}
              whileTap={{
                scale: 0.95,
                transition: MICRO_INTERACTION,
              }}
              className="cursor-pointer"
            >
              <Image
                alt="Logo Ho Van Doan"
                src="/images/logo.svg"
                width={0}
                height={0}
                priority
                sizes="100vw"
                quality={100}
                className="w-full h-auto object-fill select-none"
              />
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Menu Controls Section */}
        <motion.div
          initial={INITIAL_STATES.fade_in_start}
          animate={INITIAL_STATES.fade_in_end}
          transition={FADE_IN_TRANSITION}
          layout
        >
          <motion.div
            className="uppercase text-[13px] hover:cursor-pointer flex items-center gap-3"
            variants={hideNavItemsVariant}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              transition={MICRO_INTERACTION}
              className="flex"
            >
              <ToggleTheme />
            </motion.div>

            <motion.div
              onClick={() => setMobileNavOpen(true)}
              variants={menuIconVariant}
              whileHover={{
                scale: 1.25,
                rotate: 15,
                transition: HOVER_SPRING,
              }}
              whileTap={{
                scale: 0.9,
                rotate: -5,
                transition: MICRO_INTERACTION,
              }}
              className="cursor-pointer rounded-full hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Image
                alt="Menu Icon"
                src="/icons/menu.svg"
                quality={100}
                height={32}
                width={32}
                priority
                className="select-none"
              />
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileNavOpen && (
            <motion.div
              variants={mobileMenuVariant}
              initial="closed"
              animate="opened"
              exit="closed"
              className="mobile-menu fixed z-50 h-screen w-full flex flex-col items-center bg-white/40 dark:bg-black/40 left-0 top-0 px-[45px] py-[30px] backdrop-blur-md"
            >
              {/* Menu Header */}
              <motion.div
                className="flex w-full justify-between items-center"
                layout
              >
                <motion.div
                  className="self-start cursor-pointer w-[60px] h-[60px]"
                  variants={fadeInVariant}
                  whileHover={{
                    scale: 1.1,
                    rotate: -8,
                    y: -2,
                    transition: HOVER_SPRING,
                  }}
                  whileTap={{
                    scale: 0.95,
                    transition: MICRO_INTERACTION,
                  }}
                >
                  <Image
                    alt="Logo Ho Van Doan"
                    src="/images/logo.svg"
                    width={0}
                    height={0}
                    sizes="100vw"
                    priority
                    quality={100}
                    className="w-full h-auto object-fill select-none"
                  />
                </motion.div>

                <motion.div
                  className="self-end cursor-pointer p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                  variants={fadeInVariant}
                  onClick={() => setMobileNavOpen(false)}
                  whileHover={{
                    scale: 1.1,
                    rotate: 90,
                    transition: HOVER_SPRING,
                  }}
                  whileTap={{
                    scale: 0.9,
                    transition: MICRO_INTERACTION,
                  }}
                >
                  <Image
                    alt="Close Icon"
                    src="/icons/close.svg"
                    quality={100}
                    height={44}
                    width={44}
                    priority
                    className="select-none"
                  />
                </motion.div>
              </motion.div>

              {/* Menu Content */}
              <motion.div
                className="w-full md:flex items-center justify-center gap-0 md:gap-8 md:mt-8"
                layout
              >
                {/* Navigation Items */}
                <motion.div
                  className="w-full md:w-1/2 flex justify-center md:justify-end"
                  layout
                >
                  <motion.ul
                    variants={ulVariant}
                    className="mt-8 md:mt-0 list-none w-[345px]"
                  >
                    {MOBILE_NAV_ITEMS.map((navItem, index) => (
                      <motion.li
                        key={navItem.id}
                        className="overflow-hidden select-none mx-0 my-3 lg:my-6 cursor-pointer group"
                        custom={index}
                      >
                        <motion.div
                          className="text-center md:text-start capitalize text-[28px] md:text-[34px] font-semibold text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300"
                          variants={liVariant}
                          whileHover={{
                            x: 15,
                            scale: 1.05,
                            transition: HOVER_SPRING,
                          }}
                          whileTap={{
                            scale: 0.98,
                            x: 5,
                            transition: MICRO_INTERACTION,
                          }}
                        >
                          <motion.span
                            className="inline-block"
                            whileHover={{
                              rotateY: 10,
                              transition: SMOOTH_TRANSITION,
                            }}
                          >
                            {navItem.navTitle}
                          </motion.span>
                        </motion.div>
                      </motion.li>
                    ))}
                  </motion.ul>
                </motion.div>

                {/* Banner Section */}
                <motion.div
                  variants={fadeInVariant}
                  className="w-full md:w-1/2 mt-6 md:mt-0"
                  layout
                >
                  <motion.div
                    className="overflow-hidden rounded-2xl relative md:w-[345px] md:h-[345px] group cursor-pointer flex justify-center shadow-2xl"
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                      transition: SMOOTH_TRANSITION,
                    }}
                  >
                    <motion.div
                      variants={bannerImageVariant}
                      className="relative"
                    >
                      <Image
                        alt="Banner"
                        src="/images/avatar/hodoan-banner.jpg"
                        className="rounded-2xl object-cover w-[300px] h-[300px] md:w-[345px] md:h-[345px] select-none"
                        width={0}
                        height={0}
                        sizes="100vw"
                        priority
                        quality={100}
                      />
                    </motion.div>

                    <motion.div
                      className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-2xl"
                      variants={overlayVariant}
                      whileHover={{
                        opacity: 0.3,
                        transition: SMOOTH_TRANSITION,
                      }}
                    >
                      <motion.span
                        className="text-sm absolute right-6 top-4 font-medium px-4 py-2 rounded-full bg-white/90 text-gray-800 backdrop-blur-sm shadow-lg"
                        variants={textElementVariant}
                        whileHover={{
                          scale: 1.05,
                          y: -2,
                          transition: HOVER_SPRING,
                        }}
                      >
                        My Self
                      </motion.span>

                      <motion.h3
                        className="font-bold text-2xl md:text-3xl absolute bottom-6 left-6 text-white drop-shadow-lg"
                        variants={textElementVariant}
                        whileHover={{
                          scale: 1.02,
                          y: -2,
                          transition: HOVER_SPRING,
                        }}
                      >
                        Hồ Văn Đoan
                      </motion.h3>
                    </motion.div>
                  </motion.div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </div>
  );
}
